'use client'

import { useState, useEffect } from 'react'
import { useSession } from 'next-auth/react'
import type { Session } from 'next-auth'

type SessionUser = NonNullable<Session['user']>

interface CustomAuthState {
  isAuthenticated: boolean
  user: SessionUser | null
  isLoading: boolean
}

export const useCustomAuth = (): CustomAuthState => {
  const { data: session, status } = useSession()
  const [profileImageOverride, setProfileImageOverride] = useState<string | null>(null)
  const [authState, setAuthState] = useState<CustomAuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true
  })

  // When session has user but no image (stale JWT), fetch from DB
  useEffect(() => {
    if (status !== 'authenticated' || !session?.user?.id) {
      setProfileImageOverride(null)
      return
    }
    if (session.user.image) {
      setProfileImageOverride(null)
      return
    }
    fetch('/api/user/profile-image')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => data?.image && setProfileImageOverride(data.image))
      .catch(() => {})
  }, [status, session?.user?.id, session?.user?.image])

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') {
      return
    }

    // FIRST: Clear logout flags if NOT on logout page (prevents stale flags after login)
    const urlParams = new URLSearchParams(window.location.search)
    const isLogoutPage = window.location.pathname === '/auth' && urlParams.get('logout') === 'true'
    
    if (!isLogoutPage && typeof window !== 'undefined') {
      // Not on logout page - clear any stale logout flags
      const hadFlags = localStorage.getItem('user_logged_out') === 'true' || 
                       localStorage.getItem('nuclear_logout') === 'true' ||
                       document.cookie.includes('user_logged_out=true');
      
      if (hadFlags) {
        console.log('[CLEAN] [useCustomAuth] Clearing stale logout flags (not on logout page)');
        localStorage.removeItem('user_logged_out')
        localStorage.removeItem('logout_timestamp')
        localStorage.removeItem('nuclear_logout')
        localStorage.removeItem('session_killed')
        localStorage.removeItem('force_logout')
        sessionStorage.removeItem('session_logout')
        sessionStorage.removeItem('session_killed_flag')
        document.cookie = 'user_logged_out=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        document.cookie = 'session_killed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      }
    }

    // NOW check logout state (only relevant on logout page)
    const hasLogoutParam = urlParams.get('logout') === 'true'
    const hasNuclearParam = urlParams.get('nuclear') === '1'
    
    // Only enforce logout if we're actually on the logout page with logout param
    if (isLogoutPage && (hasLogoutParam || hasNuclearParam)) {
      console.log('[ALERT] [useCustomAuth] On logout page - showing logged out state')
      
      // Force unauthenticated state
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false
      })
      return
    }

    // PRIORITY 2: If session is still loading, wait
    if (status === 'loading') {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: true
      })
      return
    }

    // PRIORITY 3: Check if we have a valid session (but only if no logout flags)
    if (status === 'authenticated' && session?.user?.id) {
      // Additional validation: check if session is not expired
      const now = Date.now()
      const sessionExpiry = session.expires ? new Date(session.expires).getTime() : now + 24 * 60 * 60 * 1000
      
      if (now < sessionExpiry) {
        console.log('[OK] [useCustomAuth] Valid session found')
        const userWithImage = profileImageOverride
          ? { ...session.user, image: profileImageOverride }
          : session.user
        setAuthState({
          isAuthenticated: true,
          user: userWithImage,
          isLoading: false
        })
        return
      } else {
        console.log('[WARN] [useCustomAuth] Session expired')
      }
    }

    // PRIORITY 4: Default to unauthenticated
    console.log('[BLOCK] [useCustomAuth] No valid session')
    setAuthState({
      isAuthenticated: false,
      user: null,
      isLoading: false
    })
  }, [session, status, profileImageOverride])

  return authState
}
